import type { AnswerInput } from "@typedoc/contracts/chat";
import { env } from "../../env/runtime";
import { regionalOptions } from "../access-client";
import { createSampleFilesPort } from "../files/sample-port";
import type { SentFile } from "../files/types";
import { applyAction, type Action } from "../processes/draft";
import { sampleProcesses } from "../processes/sample";
import type { ProcessState, Refusal } from "../processes/types";
import { plainText } from "./answer/plain-text";
import { createLiveChatClient, type LiveChatClient } from "./live-client";
import { SAMPLE_ANSWERS, SAMPLE_RECENT_IDS, sampleArchivedChats, sampleChats } from "./mock-data";
import { parseChatScope } from "./scope";
import type {
  Chat,
  ChatFile,
  ChatMessage,
  ChatSummary,
  Mention,
  NewChat,
  Reply,
  SearchResult,
} from "./types";
import { searchChats, summarize } from "./view";

/**
 * The chat service the screens talk to, until there is one. It holds sample data in memory, answers
 * after a short wait (so the loading states are the ones a real service would show) and forgets
 * everything when the page reloads. When the chat API exists this file is the one that changes: the
 * methods and what they answer are the contract the screens use.
 */

/** How long each call takes, in milliseconds. Tests pass zeros. */
export type Latency = {
  list: number;
  search: number;
  open: number;
  write: number;
  /** Between two bursts of an answer arriving: the chat runtime flushes text about every 150ms. */
  burst: number;
};

const DEFAULT_LATENCY: Latency = { list: 350, search: 250, open: 900, write: 150, burst: 150 };

/**
 * How many characters each burst of a streamed answer carries, round and round: uneven, and cut
 * wherever the count falls, inside a word as often as not, as a model's tokens are.
 */
const BURST_SIZES: readonly number[] = [31, 44, 27, 52, 38, 33, 47, 29];

/** The text of an answer so far, and whether it is all there. */
export type Burst = { text: string; done: boolean };

/** The sidebar lists this many chats you opened last. */
const RECENT_LIMIT = 4;

/** Waits, and gives up the moment the caller does, as a request that was canceled would. */
function wait(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Canceled", "AbortError"));
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    function onAbort() {
      clearTimeout(timer);
      reject(new DOMException("Canceled", "AbortError"));
    }
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

export function createChatClient({
  now = () => new Date(),
  latency = DEFAULT_LATENCY,
}: { now?: () => Date; latency?: Latency } = {}) {
  let chats: Chat[] = [...sampleChats(now()), ...sampleArchivedChats(now())];
  let processes: Record<string, ProcessState> = sampleProcesses(now());
  let recentIds: string[] = [...SAMPLE_RECENT_IDS];
  let counter = 0;
  /** The whole text of each answer still arriving, by message id. */
  const scripts = new Map<string, string>();

  const find = (id: string) => chats.find((chat) => chat.id === id);
  const replace = (next: Chat) => {
    chats = chats.map((chat) => (chat.id === next.id ? next : chat));
  };
  const remember = (id: string) => {
    recentIds = [id, ...recentIds.filter((recent) => recent !== id)].slice(0, RECENT_LIMIT);
  };

  /** A title for a chat that has none yet: its first words, cut at a word. */
  function titleFrom(text: string): string {
    const clean = text.replace(/\s+/g, " ").trim();
    if (clean.length <= 48) return clean;
    const cut = clean.slice(0, 48);
    return `${cut.slice(0, Math.max(cut.lastIndexOf(" "), 24))}…`;
  }

  /** What a message's files add to the chat's own list of them. */
  function attached(files: readonly SentFile[], at: Date): ChatFile[] {
    return files.map((file) => ({
      id: file.id,
      name: file.name,
      at,
      mediaType: file.mediaType,
      size: file.size,
    }));
  }

  function sent(
    chat: Chat,
    text: string,
    files: readonly SentFile[] = [],
    mentions: readonly Mention[] = [],
  ): ChatMessage {
    counter += 1;
    return {
      id: `${chat.id}-m${String(chat.messages.length + 1)}-${String(counter)}`,
      author: "me",
      at: now(),
      text,
      local: true,
      ...(files.length > 0 ? { files } : {}),
      ...(mentions.length > 0 ? { mentions } : {}),
    };
  }

  /** The files of every chat made here live in this browser's memory with the chats. */
  const files = createSampleFilesPort();

  return {
    files,

    /** Every chat; the archived ones only when asked for. */
    async list(signal?: AbortSignal, archived = false): Promise<ChatSummary[]> {
      await wait(latency.list, signal);
      return chats.filter((chat) => archived || !chat.archivedAt).map(summarize);
    },

    /** The chats opened last, newest first, for the sidebar. */
    async recent(signal?: AbortSignal): Promise<ChatSummary[]> {
      await wait(latency.list, signal);
      return recentIds.flatMap((id) => {
        const chat = find(id);
        return chat && !chat.archivedAt ? [summarize(chat)] : [];
      });
    },

    async search(query: string, signal?: AbortSignal, archived = false): Promise<SearchResult[]> {
      await wait(latency.search, signal);
      return searchChats(
        chats.filter((chat) => archived || !chat.archivedAt),
        query,
      );
    },

    /** The whole chat, or null when there is none by that id. */
    async open(id: string, signal?: AbortSignal): Promise<Chat | null> {
      await wait(latency.open, signal);
      const chat = find(id) ?? null;
      if (chat) remember(chat.id);
      return chat;
    },

    /** A process and its draft, or null when there is none by that id. */
    async process(id: string, signal?: AbortSignal): Promise<ProcessState | null> {
      await wait(latency.open, signal);
      return processes[id] ?? null;
    },

    /** Applies what a person did to a draft. The answer is the draft after it, or why it was not. */
    async act(processId: string, action: Action): Promise<ProcessState | Refusal> {
      await wait(latency.write);
      const state = processes[processId];
      if (!state) return { refusal: "That playbook is not here." };
      const next = applyAction(state, action, now());
      if (!("refusal" in next)) processes = { ...processes, [processId]: next };
      return next;
    },

    /**
     * Answers a request the sample holds (only the approval in "Invoice delays" can be). Approving
     * finishes the step that waited for it; anything else, a "no", settles it as declined. The chat
     * as it is now, or null when there is no such chat or request.
     */
    async answerRequest(
      chatId: string,
      requestId: string,
      answer: AnswerInput["answer"],
    ): Promise<Chat | null> {
      await wait(latency.write);
      const chat = find(chatId);
      const asked = chat?.messages.find((message) => message.request?.id === requestId);
      if (!chat || !asked?.request) return null;
      const approved = answer.type === "choice" && answer.optionId.startsWith("approve");
      // A question's answer reaches the chat as the person's message, as the service writes it: the
      // option's words, then the note after a blank line, or the person's own words.
      const option =
        answer.type === "choice" && asked.request.response.type === "choice"
          ? asked.request.response.options.find((candidate) => candidate.id === answer.optionId)
          : undefined;
      const words =
        answer.type === "text"
          ? answer.text
          : [option?.label, answer.note?.trim()].filter(Boolean).join("\n\n");
      const reached = asked.request.kind === "approval" || words === "" ? [] : [sent(chat, words)];
      const next: Chat = {
        ...chat,
        needsYou: false,
        ...(reached[0] ? { lastAt: reached[0].at, preview: words } : {}),
        messages: [...chat.messages, ...reached].map((message) =>
          message.id !== asked.id || !message.request
            ? message
            : {
                ...message,
                request: { ...message.request, status: "answered", canAnswer: false },
                ...(message.steps
                  ? {
                      steps: message.steps.map((step) =>
                        step.status === "waiting"
                          ? {
                              ...step,
                              status: approved ? ("done" as const) : ("declined" as const),
                            }
                          : step,
                      ),
                    }
                  : {}),
              },
        ),
      };
      replace(next);
      return next;
    },

    /** Puts a chat away or brings it back. The chat as it is now, or null when there is none. */
    async setArchived(chatId: string, archived: boolean): Promise<Chat | null> {
      await wait(latency.write);
      const chat = find(chatId);
      if (!chat) return null;
      const { archivedAt: _before, ...rest } = chat;
      // Asking for what the chat already is changes nothing, and the first time stands.
      const next: Chat = archived ? { ...rest, archivedAt: chat.archivedAt ?? now() } : rest;
      replace(next);
      return next;
    },

    /** Starts a chat from a first message. Abacor does not answer it: there is nothing to answer. */
    async create(input: NewChat): Promise<Chat> {
      await wait(latency.write);
      const startedAt = now();
      counter += 1;
      const id = `new-chat-${String(counter)}`;
      const draft: Chat = {
        id,
        title: titleFrom(input.text),
        startedBy: "me",
        startedAt,
        lastAt: startedAt,
        preview: input.text,
        needsYou: false,
        files: [],
        steps: [],
        messages: [],
      };
      const chat: Chat = {
        ...draft,
        files: attached(input.files ?? [], startedAt),
        messages: [sent(draft, input.text, input.files, input.mentions)],
      };
      chats = [chat, ...chats];
      remember(chat.id);
      return chat;
    },

    /** Adds a reply to a chat. Like a new chat, it gets no answer. */
    async reply({ chatId, text, files: replyFiles = [], mentions }: Reply): Promise<Chat | null> {
      await wait(latency.write);
      const chat = find(chatId);
      if (!chat) return null;
      const message = sent(chat, text, replyFiles, mentions);
      // A chat with a scripted answer gets one, empty and on its way; the others get none.
      const script = SAMPLE_ANSWERS[chatId];
      const answer: ChatMessage | null =
        script === undefined
          ? null
          : {
              id: `${chat.id}-m${String(chat.messages.length + 2)}-${String(counter)}-answer`,
              author: "assistant",
              at: now(),
              text: "",
              streaming: true,
            };
      if (answer && script !== undefined) scripts.set(answer.id, script);
      // The message begins a new run, so the notice about the last one goes with it.
      const { stopped: _stopped, ...rest } = chat;
      const next: Chat = {
        ...rest,
        lastAt: message.at,
        preview: text,
        needsYou: false,
        files: [...chat.files, ...attached(replyFiles, message.at)],
        messages: [...chat.messages, message, ...(answer ? [answer] : [])],
      };
      replace(next);
      return next;
    },

    /**
     * The answer that is on its way, in bursts: each is the text so far. It picks up from the text
     * the chat already holds, so a page that was left and opened again goes on from there, and it
     * stops when the caller does. Nothing comes for a message that is not on its way.
     */
    async *stream(chatId: string, messageId: string, signal?: AbortSignal): AsyncGenerator<Burst> {
      const script = scripts.get(messageId);
      let length = find(chatId)?.messages.find((message) => message.id === messageId)?.text.length;
      if (script === undefined || length === undefined) return;
      for (let burst = 0; length < script.length; burst++) {
        await wait(latency.burst, signal);
        length = Math.min(script.length, length + (BURST_SIZES[burst % BURST_SIZES.length] ?? 40));
        const done = length >= script.length;
        const arrived = script.slice(0, length);
        const chat = find(chatId);
        if (chat) {
          replace({
            ...chat,
            ...(done
              ? { preview: plainText(arrived).split(/(?<=[.!?])\s/)[0] ?? chat.preview }
              : {}),
            messages: chat.messages.map((message) =>
              message.id === messageId
                ? {
                    id: message.id,
                    author: message.author,
                    at: message.at,
                    text: arrived,
                    ...(done ? {} : { streaming: true }),
                  }
                : message,
            ),
          });
        }
        if (done) scripts.delete(messageId);
        yield { text: arrived, done };
      }
    },
  };
}

export type { NewChat, Reply };

export type SampleChatClient = ReturnType<typeof createChatClient>;

/**
 * What the screens call: the sample service above, or the Chats API (`./live-client`) when the app
 * was built with `VITE_CHAT_API=live`. Both answer the same calls with the same types. They differ
 * in how an answer arrives: the sample has `stream`, which gives the text of one answer as it
 * grows, and the live service has `follow`, which gives the whole chat as the API's stream
 * changes it (`use-chat-stream.ts` reads whichever there is).
 */
export type ChatClient = SampleChatClient | LiveChatClient;

/** Which service this build talks to. Fixed when the app is built; nothing in the app changes it. */
export type ChatApi = "sample" | "live";
export const chatApi: ChatApi = env.VITE_CHAT_API ?? "sample";

type SampleOptions = Parameters<typeof createChatClient>[0];

/** The sample's process canvas, made when first used, for the live service to borrow. */
function sampleCanvas(options: SampleOptions): Pick<SampleChatClient, "process" | "act"> {
  let sample: SampleChatClient | undefined;
  const made = () => (sample ??= createChatClient(options));
  return {
    process: (id, signal) => made().process(id, signal),
    act: (id, action) => made().act(id, action),
  };
}

/** A service for one person in one workspace, of the kind the build asks for. */
export function makeChatClient(api: ChatApi, scope: string, options?: SampleOptions): ChatClient {
  if (api === "sample") return createChatClient(options);
  const { userId, workspaceId } = parseChatScope(scope);
  return createLiveChatClient({
    userId,
    workspaceId,
    options: regionalOptions,
    processes: sampleCanvas(options),
  });
}

/**
 * The service is made for whoever is signed in and the workspace they are in: one per person and
 * workspace, the same boundary the query keys use. The sample's is state a person writes into, so
 * a service shared by the page would show what was written under one account or workspace to the
 * next without a reload; the live one holds the chats it has open, and the API's token is the
 * person's too. `options` is read only when a scope's service is first made (tests pass no
 * waiting).
 */
const clients = new Map<string, ChatClient>();

export function chatClientFor(
  scope: string,
  options?: SampleOptions,
  api: ChatApi = chatApi,
): ChatClient {
  // Nothing is read or written before the person and the workspace are known.
  if (scope === "") {
    throw new Error("Chats belong to a person in a workspace; there is no scope yet");
  }
  let client = clients.get(scope);
  if (!client) {
    client = makeChatClient(api, scope, options);
    clients.set(scope, client);
  }
  return client;
}

/**
 * Sign-out: nothing written or held in this browser belongs to the next visitor of it, and nothing
 * the last one started is left running.
 */
export function forgetChatClients(): void {
  for (const client of clients.values()) {
    if ("dispose" in client) client.dispose();
  }
  clients.clear();
}
